import { db } from '../firebase';
import { doc, getDoc, collection, collectionGroup, query, getDocs, deleteDoc, limit } from 'firebase/firestore';
import { deleteImage } from './imageService';

/** Shuffle array (Fisher-Yates). */
function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * Obtiene URLs de imágenes de portfolios de todos los usuarios para el hero.
 * Usa collectionGroup para leer la subcolección portfolioMedia de todos los usuarios.
 * @param {number} limitCount - Número máximo de URLs a devolver (ej. 15)
 * @returns {Promise<string[]>}
 */
export const getHeroPortfolioImageUrls = async (limitCount = 15) => {
  try {
    const portfolioMediaRef = collectionGroup(db, 'portfolioMedia');
    const q = query(portfolioMediaRef, limit(80));
    const snapshot = await getDocs(q);
    const urls = snapshot.docs
      .map((d) => {
        const data = d.data();
        return data.thumbnailUrl || data.thumb || data.url || data.imageUrl || data.src || null;
      })
      .filter(Boolean);
    return shuffle(urls).slice(0, limitCount);
  } catch (error) {
    console.warn('getHeroPortfolioImageUrls:', error?.message || error);
    return [];
  }
};

/**
 * Obtener portfolio completo de un usuario
 * @param {string} userId - ID del usuario
 * @returns {Promise<Object>} Portfolio completo con secciones y media
 */
export const getUserPortfolio = async (userId) => {
  try {
    console.log('📂 Obteniendo portfolio para userId:', userId);

    // 1. Obtener documento del usuario (contiene portfolioSections)
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);

    if (!userDoc.exists()) {
      console.warn('⚠️ Usuario no encontrado');
      return {
        success: false,
        error: 'Usuario no encontrado',
        portfolioSections: null,
        portfolioMedia: []
      };
    }

    const userData = userDoc.data();
    const portfolioSections = userData.portfolioSections || null;

    // 2. Obtener subcolección portfolioMedia
    let portfolioMedia = [];
    
    try {
      const portfolioMediaRef = collection(db, 'users', userId, 'portfolioMedia');
      
      // Obtener SIN orderBy (más simple y compatible)
      const portfolioMediaSnapshot = await getDocs(portfolioMediaRef);
      
      portfolioMedia = portfolioMediaSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Ordenar manualmente por el campo 'order' si existe
      portfolioMedia.sort((a, b) => {
        const orderA = a.order !== undefined ? a.order : 999999;
        const orderB = b.order !== undefined ? b.order : 999999;
        return orderA - orderB;
      });
      
      console.log(`✅ Portfolio obtenido: ${portfolioMedia.length} items de media`);
    } catch (mediaError) {
      console.warn('⚠️ No se pudo obtener portfolioMedia:', mediaError.message);
      portfolioMedia = [];
    }

    return {
      success: true,
      portfolioSections,
      portfolioMedia,
      hasPortfolio: portfolioSections !== null || portfolioMedia.length > 0
    };
  } catch (error) {
    console.error('❌ Error al obtener portfolio:', error);
    return {
      success: false,
      error: error.message,
      portfolioSections: null,
      portfolioMedia: []
    };
  }
};

/**
 * Normalizar datos del portfolio para visualización
 * @param {Object} portfolioSections - Secciones del portfolio
 * @param {Array} portfolioMedia - Media del portfolio
 * @returns {Object} Portfolio normalizado
 */
export const normalizePortfolio = (portfolioSections, portfolioMedia) => {
  // Estructura unificada del portfolio
  const normalized = {
    sections: portfolioSections || {},
    media: portfolioMedia || [],
    // Extraer información específica si existe
    experience: portfolioSections?.experience || [],
    education: portfolioSections?.education || [],
    skills: portfolioSections?.skills || [],
    projects: portfolioSections?.projects || [],
    certifications: portfolioSections?.certifications || [],
    languages: portfolioSections?.languages || [],
    awards: portfolioSections?.awards || [],
  };

  return normalized;
};

/**
 * Eliminar una imagen del portfolio del usuario
 * Borra el documento en Firestore y, si existe url de Firebase Storage, el archivo en Storage
 * @param {string} userId - ID del usuario
 * @param {string} itemId - ID del documento en portfolioMedia
 * @param {string} [imageUrl] - URL de la imagen (opcional, para borrar de Storage)
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export const deletePortfolioMedia = async (userId, itemId, imageUrl) => {
  try {
    const mediaRef = doc(db, 'users', userId, 'portfolioMedia', itemId);
    if (imageUrl && typeof imageUrl === 'string' && imageUrl.includes('firebasestorage.googleapis.com')) {
      const del = await deleteImage(imageUrl);
      if (!del.success) {
        console.warn('No se pudo eliminar la imagen de Storage:', del.error);
      }
    }
    await deleteDoc(mediaRef);
    return { success: true };
  } catch (error) {
    console.error('Error al eliminar imagen del portfolio:', error);
    return { success: false, error: error.message };
  }
};
